/**
 * Aggregation for the BHCS Cash overview on AdminBalances.
 *
 * The per-parent liability table answers "how much does each family hold right
 * now". These functions answer "what has been moving", which needs a time
 * dimension and a cross-parent view — neither of which the snapshot provides.
 *
 * Pure and exported so the arithmetic is unit-tested rather than mirrored in a
 * test file.
 */

/** Credit sources that represent real money a parent put in. */
export const PURCHASE_SOURCE_TYPES = ['stripe_payment', 'zelle_payment'];

/** An ACH top-up older than this has outrun normal settlement and needs a look. */
export const ACH_STALE_DAYS = 6;

export type CashTransactionType = 'credit' | 'debit' | 'refund' | 'withdrawal';

export interface CashActivityTx {
  type: CashTransactionType;
  amount_cents: number;
  source_ref_type: string | null;
}

export interface CashActivityTotals {
  /** Real money in, via card / ACH / Zelle. */
  funded: number;
  /** School-funded purchase bonus — not money the school received. */
  bonus: number;
  /** Admin grants, corrections, and other non-purchase credits. */
  granted: number;
  /** Applied to invoices. */
  spent: number;
  /** Cashed out to the parent. */
  withdrawn: number;
  refunded: number;
}

export function summarizeCashActivity(txs: CashActivityTx[]): CashActivityTotals {
  const totals: CashActivityTotals = {
    funded: 0, bonus: 0, granted: 0, spent: 0, withdrawn: 0, refunded: 0,
  };

  for (const tx of txs) {
    const src = tx.source_ref_type ?? '';
    if (tx.type === 'credit' && PURCHASE_SOURCE_TYPES.includes(src)) totals.funded += tx.amount_cents;
    else if (tx.type === 'credit' && src === 'purchase_bonus') totals.bonus += tx.amount_cents;
    else if (tx.type === 'credit') totals.granted += tx.amount_cents;
    else if (tx.type === 'debit') totals.spent += tx.amount_cents;
    else if (tx.type === 'withdrawal') totals.withdrawn += tx.amount_cents;
    else if (tx.type === 'refund') totals.refunded += tx.amount_cents;
  }

  return totals;
}

/** Whole days a pending ACH purchase has been in flight. */
export function achAgeDays(createdAt: string, now: number = Date.now()): number {
  return Math.floor((now - new Date(createdAt).getTime()) / 86_400_000);
}

/** Pending ACH purchases that have been in flight longer than normal settlement. */
export function countStaleAch(
  rows: Array<{ created_at: string }>,
  now: number = Date.now(),
): number {
  return rows.filter((r) => achAgeDays(r.created_at, now) > ACH_STALE_DAYS).length;
}
